import React from 'react';
import { motion } from 'framer-motion';
import { 
  DocumentTextIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';

const TermsOfServicePage: React.FC = () => {
  const lastUpdated = 'December 15, 2024';

  const sections = [
    {
      title: 'Acceptance of Terms',
      icon: CheckCircleIcon,
      content: [
        'By accessing and using StyleSync, you accept and agree to be bound by these Terms of Service',
        'If you do not agree to these terms, you must not use our services',
        'These terms apply to all users, visitors, and others who access or use the service',
        'We reserve the right to modify these terms at any time'
      ]
    },
    {
      title: 'Use License',
      icon: DocumentTextIcon,
      content: [
        'StyleSync grants you a limited, non-exclusive, non-transferable license to use the service',
        'You may use the service for lawful purposes only',
        'You are responsible for maintaining the confidentiality of your account',
        'You must not share your account credentials with others'
      ]
    },
    {
      title: 'Prohibited Uses',
      icon: XCircleIcon,
      content: [
        'Using the service for any unlawful purpose or to solicit others to perform unlawful acts',
        'Violating any international, federal, provincial, or state regulations, rules, or laws',
        'Infringing upon or violating our intellectual property rights or the intellectual property rights of others',
        'Harassing, abusing, insulting, harming, defaming, or discriminating against others'
      ]
    },
    {
      title: 'User Content',
      icon: DocumentTextIcon,
      content: [
        'You retain ownership of content you create and share on StyleSync',
        'You grant us a license to use, store, and display your content to provide our services',
        'You are responsible for the content you upload and share',
        'We may remove content that violates our policies or terms'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
              <ScaleIcon className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
          <p className="text-xl text-gray-600 mb-4">
            Please read these terms carefully before using StyleSync. These terms govern your use of our service.
          </p>
          <div className="flex items-center justify-center text-sm text-gray-500">
            <CalendarIcon className="w-4 h-4 mr-2" />
            Last updated: {lastUpdated}
          </div>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8"
        >
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Agreement to Terms</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            These Terms of Service ("Terms") govern your use of the StyleSync platform and services. 
            By accessing or using StyleSync, you agree to be bound by these Terms and our Privacy Policy.
          </p>
          <p className="text-gray-600 leading-relaxed">
            StyleSync is a collaborative design platform that enables teams to create, share, and manage 
            design projects. These terms outline your rights and responsibilities when using our service.
          </p>
        </motion.div>

        {/* Terms Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mr-4">
                  <section.icon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900">{section.title}</h2>
              </div>
              <ul className="space-y-3">
                {section.content.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Additional Terms */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mt-8"
        >
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Service Terms</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Account Responsibilities</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Provide accurate registration information</li>
                <li>• Maintain account security</li>
                <li>• Notify us of unauthorized access</li>
                <li>• Comply with all applicable laws</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Service Availability</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• We strive for 99.9% uptime</li>
                <li>• Scheduled maintenance notifications</li>
                <li>• Emergency maintenance may occur</li>
                <li>• Service updates and improvements</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Payment Terms */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border border-green-200 p-8 mt-8"
        >
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Payment and Billing</h2>
          <div className="space-y-4 text-gray-600">
            <p><strong>Free Tier:</strong> Basic features available at no cost with limitations.</p>
            <p><strong>Premium Plans:</strong> Advanced features available through paid subscriptions.</p>
            <p><strong>Billing:</strong> Recurring charges based on your selected plan.</p>
            <p><strong>Cancellation:</strong> You may cancel your subscription at any time.</p>
            <p><strong>Refunds:</strong> Refund policies vary by plan and usage.</p>
          </div>
        </motion.div>

        {/* Limitation of Liability */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="bg-yellow-50 border border-yellow-200 rounded-xl p-8 mt-8"
        >
          <div className="flex items-center mb-4">
            <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 mr-3" />
            <h2 className="text-2xl font-semibold text-yellow-800">Limitation of Liability</h2>
          </div>
          <p className="text-yellow-700 leading-relaxed">
            StyleSync shall not be liable for any indirect, incidental, special, consequential, or punitive damages, 
            including without limitation, loss of profits, data, use, goodwill, or other intangible losses, 
            resulting from your use of the service.
          </p>
        </motion.div>

        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
          className="bg-blue-50 border border-blue-200 rounded-xl p-8 mt-8"
        >
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Questions About These Terms?</h2>
          <p className="text-gray-600 mb-4">
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <div className="space-y-2 text-gray-600">
            <p><strong>Email:</strong> legal@stylesync.com</p>
            <p><strong>Address:</strong> StyleSync Inc., Toronto, Ontario, Canada</p>
            <p><strong>Phone:</strong> +1 (555) 123-4567</p>
          </div>
        </motion.div>
      </main>

      <AppFooter />
    </div>
  );
};

export default TermsOfServicePage; 